import { Controller, Control, FieldValues, RegisterOptions, Path } from 'react-hook-form';
import { Autocomplete, TAutocompleteOption } from '@weareopenr/component-library';
import { useState, type Dispatch, type SetStateAction } from 'react';

type OptionsProp = { name: string; id: string };

type FormAutocompleteProps<T extends FieldValues = FieldValues> = {
  name: Path<T>;
  control?: Control<T>;
  label: string;
  testId?: string;
  options: OptionsProp[];
  disabled?: boolean;
  rules?: RegisterOptions<T, Path<T>>;
  onLoadMore?: () => void;
  setSearchTerm?: Dispatch<SetStateAction<string>>;
  forceBottomPlacement?: boolean;
  className?: string;
};

export const FormAutocomplete = <T extends FieldValues = FieldValues>({
  name,
  control,
  label,
  testId,
  options = [],
  disabled,
  rules,
  onLoadMore,
  setSearchTerm,
  forceBottomPlacement,
  className,
}: FormAutocompleteProps<T>) => {
  const [hasInputText, setHasInputText] = useState(false);

  const autocompleteOptions: TAutocompleteOption[] = options.map((option) => ({
    value: option.id,
    label: option.name,
  }));

  return (
    <Controller<T>
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <div className={className} data-testid={testId}>
          <Autocomplete
            name={name}
            label={label}
            options={autocompleteOptions}
            value={field.value || undefined}
            disabled={disabled}
            error={hasInputText ? undefined : error?.message}
            filter={setSearchTerm ? 'none' : 'auto'}
            placement={forceBottomPlacement ? 'bottom' : 'auto'}
            onSearchChange={(term) => {
              setHasInputText(term !== '');
              setSearchTerm?.(term);
            }}
            onLoadMore={onLoadMore}
            onChange={(value) => field.onChange(value ?? null)}
          />
        </div>
      )}
    />
  );
};
